import * as React from 'react';
import { CancelRounded } from '@mui/icons-material';
import { Dialog, DialogTitle, Typography, DialogContent, Button, TextField } from '@mui/material';
import { EventBus } from '@/shared/utils';
import {
  DynamicPageEventKeys,
  DynamicPageEvents,
} from 'src/components/containers/dynamic-page/events';
import { CheckoutSubscription } from 'src/components/containers/dynamic-page/types';
import { z } from 'zod';

const EmailSchema = z
  .string()
  .min(1, "L'email è obbligatoria")
  .email('Inserisci un indirizzo e-mail valido');

interface GuestCheckoutSubscriptionModalProps {
  eventBus: EventBus<DynamicPageEvents>;
  open: boolean;
  toggleModal: () => void;
  subscriptionData: CheckoutSubscription | undefined;
}

export const GuestCheckoutSubscriptionModal: React.FC<GuestCheckoutSubscriptionModalProps> = ({
  eventBus,
  open,
  toggleModal,
  subscriptionData,
}) => {
  const beginGuestCheckout = async (email: string) => {
    if (subscriptionData)
      eventBus.emit(DynamicPageEventKeys.checkout.getGuestSubscription, {
        ...subscriptionData,
        email,
      });
  };

  const [email, setEmail] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    const result = EmailSchema.safeParse(email);
    if (result.success) {
      beginGuestCheckout(result.data);
    } else {
      setEmailError(result.error.errors[0].message);
    }
  };

  if (!subscriptionData) return null;
  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={toggleModal}
      aria-labelledby="abbonati-dialog-title"
      aria-describedby="abbonati-dialog-description"
    >
      <DialogTitle id="abbonati-dialog-title" className="flex justify-end" component="div">
        <CancelRounded className="text-action-divider cursor-pointer" onClick={toggleModal} />
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-8 md:px-16 md:py-12 rounded-xl bg-paper">
            <div className="flex flex-col items-center gap-2 mb-12 text-center">
              <Typography variant="mac-sans-h3" component="h1" color="textPrimary">
                7 giorni di prova. Zero costi oggi.
              </Typography>
              <Typography variant="body-500" component="p" color="textSecondary">
                Inserisci la tua email per iniziare. Esplora liberamente tutto Vibly gratuitamente
                per 7 giorni. Annulla quando vuoi, nessun impegno.
              </Typography>
            </div>
            <div className="mb-8">
              <div className="flex flex-col gap-5">
                <TextField
                  autoFocus
                  required
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder={'Il tuo indirizzo e-mail'}
                  variant="outlined"
                  size="medium"
                  className="max-md:max-w-full"
                  label={<Typography variant="s2">La tua e-mail</Typography>}
                  error={!!emailError}
                  helperText={emailError ?? ' '}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <Button
                size="large"
                variant="contained"
                type="submit"
                className="w-full whitespace-nowrap"
                onClick={handleSubmit}
              >
                <Typography variant="button-l">Inizia la prova gratuita</Typography>
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
